/**
 * Keeping a pilot session alive, using the mechanism that already exists.
 *
 * WHAT WAS WRONG BEFORE.
 * `POST /api/v1/admin/auth/login` returns `access_token`, **`refresh_token`**,
 * `expires_in` and `expires_at`. The CMS stored only the access token, so when
 * it expired the session simply ended — mid-demo on a long pilot — and the
 * earlier audit recorded this as "blocked on a missing `/auth/refresh` route on
 * the Admin API".
 *
 * That conclusion was wrong, and this module is the correction. These are
 * **Supabase** tokens: the Admin API's own dependency verifies the access token
 * against the Supabase JWKS (ES256), and the refresh token it hands back is a
 * Supabase refresh token. Supabase's auth service already exposes the exchange
 *
 *     POST {SUPABASE_URL}/auth/v1/token?grant_type=refresh_token
 *
 * and this deployment already has both values it needs to call it —
 * `NEXT_PUBLIC_SUPABASE_URL` and `NEXT_PUBLIC_SUPABASE_ANON_KEY` are configured
 * (`.env.example` ships them, and `lib/supabase/client.tsx` was written against
 * them). So no new Admin API route is required, and none was added: the CMS
 * renews the session with the provider that issued it.
 *
 * WHY A DIRECT `fetch` RATHER THAN `supabase-js`.
 * `createBrowserClient` would work, but it maintains its own session store in
 * cookies and localStorage under `sb_*` keys. The CMS already has a session
 * store (`lib/auth/token.ts`, read by both the API client and `middleware.ts`),
 * and two stores that can disagree about who is signed in is a worse problem
 * than the one being solved. One documented POST keeps a single source of
 * truth. `@supabase/ssr` stays available for anything that genuinely wants a
 * Supabase client.
 *
 * NO SILENT DEGRADATION. If the Supabase configuration is absent — a deployment
 * pointed at a different identity provider, say — `canRefresh()` is false, the
 * scheduler does not run, and the session behaves exactly as it did before. The
 * sign-in page and the gap document state that, rather than the UI implying a
 * renewal that cannot happen.
 */

import { getRefreshToken, saveSession, type StoredUser } from './token';

const SUPABASE_URL = process.env.NEXT_PUBLIC_SUPABASE_URL ?? '';
const SUPABASE_ANON_KEY = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY ?? '';

/** Is a renewal even possible in this deployment? */
export function canRefresh(): boolean {
  return Boolean(SUPABASE_URL && SUPABASE_ANON_KEY && getRefreshToken());
}

interface SupabaseTokenResponse {
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
  user?: { id?: string; email?: string | null; user_metadata?: { full_name?: string | null } };
}

/**
 * One refresh in flight at a time.
 *
 * Several requests can 401 in the same instant — a dashboard fires four — and
 * each would otherwise start its own exchange. The first refresh token wins and
 * the rest are invalidated by Supabase's rotation, which would sign the
 * operator out *because* the CMS tried to keep them signed in.
 */
let inFlight: Promise<boolean> | null = null;

/**
 * Exchange the stored refresh token for a new access token.
 *
 * Resolves `true` when the session was renewed. Resolves `false` — never
 * throws — when it could not be: no configuration, no refresh token, or
 * Supabase refusing the token (revoked, already rotated, or expired). A refusal
 * is a real end of session, and the caller's next 401 handles it.
 */
export function refreshSession(): Promise<boolean> {
  if (inFlight) return inFlight;

  inFlight = (async () => {
    const refreshToken = getRefreshToken();
    if (!SUPABASE_URL || !SUPABASE_ANON_KEY || !refreshToken) return false;

    try {
      const response = await fetch(
        `${SUPABASE_URL.replace(/\/$/, '')}/auth/v1/token?grant_type=refresh_token`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            // Supabase requires the anon key on the auth endpoints even when a
            // refresh token is supplied; it identifies the project, not the user.
            apikey: SUPABASE_ANON_KEY,
            Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ refresh_token: refreshToken }),
        }
      );

      if (!response.ok) return false;

      const session = (await response.json()) as SupabaseTokenResponse;
      if (!session.access_token) return false;

      // The identity is only overwritten when the response carries one, so a
      // renewal never blanks the name in the header.
      const user: StoredUser | undefined = session.user?.id
        ? {
            id: session.user.id,
            email: session.user.email ?? null,
            full_name: session.user.user_metadata?.full_name ?? null,
          }
        : undefined;

      saveSession(session.access_token, session.expires_in ?? 3600, user, session.refresh_token);
      return true;
    } catch {
      // Transport failure. Not a signed-out session — the caller keeps the
      // token it has and the next request decides.
      return false;
    }
  })().finally(() => {
    inFlight = null;
  });

  return inFlight;
}

/**
 * How long before expiry a renewal is attempted.
 *
 * Supabase issues one-hour access tokens by default. Five minutes is long
 * enough that an in-flight request cannot outlive the token it was sent with,
 * and short enough that a laptop asleep for the afternoon does not wake up to a
 * queue of refreshes.
 */
export const REFRESH_MARGIN_SECONDS = 5 * 60;
